import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

import type { FxConversionRate } from 'services/payment-intl';
import { MoneyGramAccountInfo, PaymentType } from 'types/payments';

import { setActiveResponse, setApplicationData } from './applicationSlice';

export type InternationalPaymentStatus = 'idle' | 'processing' | 'success' | 'failed';

export type PaymentAccount = {
  expMonth: number;
  expYear: number;
  id: number;
  mask: string;
  name: string;
  paymentTypeId: number;
  paymentTypeName: string;
  type: string;
};

export type PaymentStore = {
  responseId?: string;
  paymentTypes: PaymentType[];
  selectedPaymentType: string | null;
  paymentInfo: any;
  savePaymentInfo: boolean;
  termsAndConditionAck: boolean;
  error: string[];
  paymentAccount: PaymentAccount | null;
  paymentSettings: { [key: string]: any };
  moneyGramAccountInfo: MoneyGramAccountInfo | null;
  internationalPaymentStatus?: InternationalPaymentStatus;
  internationalPaymentError?: string | null;
  internationalPaymentId?: string | null;
  internationalPaymentFormValid?: boolean;
  internationalTransferMateFields?: Record<string, any>;
  internationalTransferMateRate?: FxConversionRate | null;
};

const initialState: PaymentStore = {
  responseId: '',
  paymentTypes: [],
  selectedPaymentType: null,
  paymentInfo: null,
  savePaymentInfo: false,
  termsAndConditionAck: false,
  error: [],
  paymentAccount: null,
  paymentSettings: {},
  moneyGramAccountInfo: null,
  internationalPaymentStatus: 'idle',
  internationalPaymentError: null,
  internationalPaymentId: null,
  internationalPaymentFormValid: false,
  internationalTransferMateFields: {},
  internationalTransferMateRate: null,
};

const paymentSlice = createSlice({
  name: 'paymentState',
  initialState,
  reducers: {
    setPaymentType: (state, action: PayloadAction<PaymentType[]>) => {
      return { ...state, paymentTypes: action.payload };
    },
    changePaymentType: (state, action: PayloadAction<string>) => {
      const {
        selectedPaymentType: oldPaymentType,
        paymentInfo,
        error,
        moneyGramAccountInfo,
      } = state;

      const isChanged = oldPaymentType !== action.payload;
      const { payload } = action;

      return {
        ...state,
        selectedPaymentType: isChanged ? payload : oldPaymentType,
        termsAndConditionAck: isChanged ? false : state.termsAndConditionAck,
        paymentInfo: isChanged ? null : paymentInfo,
        error: isChanged ? [] : error,
        moneyGramAccountInfo: isChanged ? null : moneyGramAccountInfo,
      };
    },
    setPaymentError: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        error: action.payload,
      };
    },
    updatePaymentInfo: (state, action: PayloadAction<{ [key: string]: any }>) => {
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          ...action.payload,
        },
      };
    },
    resetPaymentInfo: (state) => {
      return {
        ...state,
        paymentInfo: null,
      };
    },
    updateTermsAndConditionAck(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        termsAndConditionAck: action.payload,
      };
    },
    savePaymentInformation: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        savePaymentInfo: action.payload,
      };
    },
    setPaymentAccount: (state, action: PayloadAction<PaymentAccount>) => {
      return {
        ...state,
        paymentAccount: action.payload,
      };
    },
    setPaymentSettings: (state, action: PayloadAction<{ [key: string]: any }>) => {
      return {
        ...state,
        paymentSettings: action.payload,
      };
    },
    updateMoneyGramAccountInfo: (state, action: PayloadAction<MoneyGramAccountInfo>) => {
      return { ...state, moneyGramAccountInfo: action.payload };
    },
    setInternationalPaymentStatus: (
      state,
      action: PayloadAction<Exclude<InternationalPaymentStatus, 'success'>>,
    ) => {
      return { ...state, internationalPaymentStatus: action.payload };
    },
    confirmInternationalPayment: (state, action: PayloadAction<string>) => {
      if (!action.payload || state.responseId !== action.payload) return state;
      return { ...state, internationalPaymentStatus: 'success' };
    },
    setInternationalPaymentError: (state, action: PayloadAction<string | null>) => {
      return { ...state, internationalPaymentError: action.payload };
    },
    setInternationalPaymentId: (state, action: PayloadAction<string | null>) => {
      return { ...state, internationalPaymentId: action.payload };
    },
    setInternationalPaymentFormValid: (state, action: PayloadAction<boolean>) => {
      return { ...state, internationalPaymentFormValid: action.payload };
    },
    setInternationalTransferMateFields: (state, action: PayloadAction<Record<string, any>>) => {
      return { ...state, internationalTransferMateFields: action.payload };
    },
    setInternationalTransferMateRate: (state, action: PayloadAction<FxConversionRate | null>) => {
      return { ...state, internationalTransferMateRate: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
    builder.addCase(setApplicationData, (state, { payload }) => {
      const responseId = payload.activeResponseId;
      if (!responseId || responseId === state.responseId) return state;
      return { ...initialState, responseId };
    });
    builder.addCase(setActiveResponse, (state, { payload: responseId }) => {
      if (responseId === state.responseId) return state;
      return { ...initialState, responseId };
    });
  },
});

export const {
  confirmInternationalPayment,
  changePaymentType,
  resetPaymentInfo,
  savePaymentInformation,
  setPaymentAccount,
  setPaymentError,
  setPaymentType,
  updatePaymentInfo,
  updateTermsAndConditionAck,
  setPaymentSettings,
  updateMoneyGramAccountInfo,
  setInternationalPaymentStatus,
  setInternationalPaymentError,
  setInternationalPaymentId,
  setInternationalPaymentFormValid,
  setInternationalTransferMateFields,
  setInternationalTransferMateRate,
} = paymentSlice.actions;
export default paymentSlice.reducer;
